import { create } from 'zustand';
import { getVehicles } from '@/api/generated/vehicles/vehicles';
import type {
  FacilityFlowCoreModulesFleetDtosCreateVehicleCheckRequest,
  FacilityFlowCoreModulesFleetDtosCreateVehicleRequest,
  FacilityFlowCoreModulesFleetDtosUpdateVehicleRequest,
  FacilityFlowCoreModulesFleetDtosVehicleCheckDto,
  FacilityFlowCoreModulesFleetDtosVehicleDto,
} from '@/api/generated/models';
import { showApiError } from '@/errors/showApiError';
import { i18n } from '@/i18n';
import { useToastStore } from '@/store/useToastStore';

export type VehicleDto = FacilityFlowCoreModulesFleetDtosVehicleDto;

const api = getVehicles();

function byLicensePlate(a: VehicleDto, b: VehicleDto): number {
  return (a.licensePlate ?? '').localeCompare(b.licensePlate ?? '', 'de');
}

interface VehicleState {
  /** All vehicles incl. deactivated ones — the admin page's list. */
  vehicles: VehicleDto[];
  loading: boolean;
  saving: boolean;
  error: string | null;

  fetchVehicles: () => Promise<void>;
  /**
   * Active vehicles for the clock-in/out choice. Not cached: an admin may have added or
   * deactivated a vehicle since the last stamp.
   */
  fetchActiveVehicles: () => Promise<VehicleDto[]>;
  createVehicle: (
    data: FacilityFlowCoreModulesFleetDtosCreateVehicleRequest,
  ) => Promise<VehicleDto | null>;
  updateVehicle: (
    id: string,
    data: FacilityFlowCoreModulesFleetDtosUpdateVehicleRequest,
  ) => Promise<VehicleDto | null>;
  /** All first aid kit, inspection and vignette checks of a vehicle, newest first (FAFD-69). */
  fetchChecks: (vehicleId: string) => Promise<FacilityFlowCoreModulesFleetDtosVehicleCheckDto[]>;
  /** Records a check; the vehicle list is reloaded so its badges show the new check. */
  recordCheck: (
    vehicleId: string,
    data: FacilityFlowCoreModulesFleetDtosCreateVehicleCheckRequest,
  ) => Promise<FacilityFlowCoreModulesFleetDtosVehicleCheckDto | null>;
}

export const useVehicleStore = create<VehicleState>((set, get) => ({
  vehicles: [],
  loading: false,
  saving: false,
  error: null,

  fetchVehicles: async () => {
    set({ loading: true, error: null });
    try {
      const data = await api.getApiV1Vehicles({ activeOnly: false });
      set({ vehicles: [...data].sort(byLicensePlate), loading: false });
    } catch {
      set({ error: 'vehicles:errors.fetchFailed', loading: false });
    }
  },

  fetchActiveVehicles: async () => {
    const data = await api.getApiV1Vehicles({ activeOnly: true });
    return [...data].sort(byLicensePlate);
  },

  createVehicle: async (data) => {
    set({ saving: true });
    try {
      const created = await api.postApiV1Vehicles(data);
      set((s) => ({ vehicles: [...s.vehicles, created].sort(byLicensePlate), saving: false }));
      useToastStore.getState().addToast(i18n.t('vehicles:messages.created'), 'success');
      return created;
    } catch (err) {
      set({ saving: false });
      showApiError(err, { skipIfFieldErrors: false });
      return null;
    }
  },

  updateVehicle: async (id, data) => {
    set({ saving: true });
    try {
      const updated = await api.putApiV1VehiclesId(id, data);
      // why: the PUT answer carries no open trip, last destination or checks — keep the listed ones.
      set((s) => ({
        vehicles: s.vehicles
          .map((v) =>
            v.id === id
              ? { ...updated, openTrip: v.openTrip, lastDestination: v.lastDestination, currentChecks: v.currentChecks }
              : v,
          )
          .sort(byLicensePlate),
        saving: false,
      }));
      useToastStore.getState().addToast(i18n.t('vehicles:messages.updated'), 'success');
      return updated;
    } catch (err) {
      set({ saving: false });
      showApiError(err, { skipIfFieldErrors: false });
      return null;
    }
  },

  fetchChecks: (vehicleId) => api.getApiV1VehiclesIdChecks(vehicleId),

  recordCheck: async (vehicleId, data) => {
    set({ saving: true });
    let created: FacilityFlowCoreModulesFleetDtosVehicleCheckDto;
    try {
      created = await api.postApiV1VehiclesIdChecks(vehicleId, data);
    } catch (err) {
      set({ saving: false });
      showApiError(err, { skipIfFieldErrors: false });
      return null;
    }
    set({ saving: false });
    useToastStore.getState().addToast(i18n.t('vehicles:checks.messages.recorded'), 'success');
    await get().fetchVehicles();
    return created;
  },
}));

export default useVehicleStore;
